import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {ThemeContextType, useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationOverlayTypesEnum} from '../../../../../models/api/conversationOverlayModel';

/*
 * Props.
 */

export interface CellOverlayProps {
  type: ConversationOverlayTypesEnum;
  conversationId: number;
  requester?: TeammateModel;
  assignee?: TeammateModel;
  inbox?: InboxModel;
  isSelected?: boolean;
  isHorizontal?: boolean;
}

type ThemedCellOverlayProps = CellOverlayProps & ThemeContextType;

/*
 * Style.
 */

interface OverlayStyleProps {
  $isSelected?: boolean;
  $isHorizontal?: boolean;
}
const StyledOverlayDiv = styled.div<OverlayStyleProps>`
  ${(p) => addLayoutStyles(p.$isHorizontal)};

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => p.theme.cell.overlayBackground};

  ${(p) => addSelectedStyles(p.$isSelected)};
`;

function addLayoutStyles(isHorizontal?: boolean) {
  if (isHorizontal) {
    return css`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 8px;
    `;
  }

  return css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `;
}

function addSelectedStyles(isSelected?: boolean) {
  if (!isSelected) {
    return '';
  }

  return css`
    color: ${(p) => p.theme.invertedText};
    background-color: ${(p) => p.theme.palette.blue.shade40};
  `;
}

const StyledDescriptionDiv = styled.div<OverlayStyleProps>`
  max-width: 100%;
  margin-top: ${(p) => (p.$isHorizontal ? '0' : '8px')};
  padding: ${(p) => (p.$isHorizontal ? '' : '0 30px')};

  font-style: italic;
  font-weight: ${fontWeights.normal};

  ${ellipsis()};

  strong {
    font-weight: ${fontWeights.semibold};
  }
`;

/*
 * Component.
 */

export const CellOverlay: FC<CellOverlayProps> = (props) => {
  const theme = useUserTheme();
  return (
    <StyledOverlayDiv
      {...buildInteractionIdProps(`conversation-overlay-${props.conversationId}`)}
      $isSelected={props.isSelected}
      $isHorizontal={props.isHorizontal}
    >
      {overlayRenderers[props.type]({...props, theme})}
    </StyledOverlayDiv>
  );
};

type OverlayRendererMap = {[T in ConversationOverlayTypesEnum]: RendererOf<ThemedCellOverlayProps>};
const overlayRenderers: OverlayRendererMap = {
  [ConversationOverlayTypesEnum.ASSIGNED]: renderAssignedOverlay,
  [ConversationOverlayTypesEnum.MOVED]: renderMovedOverlay,
  [ConversationOverlayTypesEnum.ARCHIVED]: renderArchivedOverlay,
  [ConversationOverlayTypesEnum.SNOOZED]: renderSnoozedOverlay,
  [ConversationOverlayTypesEnum.TRASHED]: renderTrashedOverlay,
  [ConversationOverlayTypesEnum.RESTORED]: renderRestoredOverlay,
  [ConversationOverlayTypesEnum.REOPENED]: renderRestoredOverlay,
};

/*
 * Assignee.
 */

function renderAssignedOverlay({assignee, isHorizontal}: ThemedCellOverlayProps) {
  if (!assignee) {
    return null;
  }

  return (
    <>
      <TeammateAvatar teammate={assignee} size={VisualSizesEnum.MEDIUM} />
      <StyledDescriptionDiv $isHorizontal={isHorizontal}>
        <Trans id="cell-overlay-assigned">
          <strong>Assigned</strong> to {buildTeammateDisplayName(assignee)}
        </Trans>
      </StyledDescriptionDiv>
    </>
  );
}

/*
 * Inbox.
 */

function renderMovedOverlay({inbox, isSelected, theme, isHorizontal}: ThemedCellOverlayProps) {
  if (!inbox) {
    return null;
  }

  const iconColor = isSelected ? theme.invertedText : theme.greys.shade70;

  return (
    <>
      <Icon name="inbox" color={iconColor} />
      <StyledDescriptionDiv $isHorizontal={isHorizontal}>
        <Trans id="cell-overlay-moved">
          <strong>Moved</strong> to {inbox.name}
        </Trans>
      </StyledDescriptionDiv>
    </>
  );
}

/*
 * Status.
 */

function renderArchivedOverlay({requester, isSelected, theme, isHorizontal}: ThemedCellOverlayProps) {
  const actionName = <Trans id="cell-overlay-archived">Archived</Trans>;
  const iconColor = isSelected ? theme.invertedText : theme.palette.green.shade40;
  return renderStatusOverlay('archiveFilled', iconColor, actionName, requester, isHorizontal);
}

function renderSnoozedOverlay({requester, isSelected, theme, isHorizontal}: ThemedCellOverlayProps) {
  const actionName = <Trans id="cell-overlay-snoozed">Snoozed</Trans>;
  const iconColor = isSelected ? theme.invertedText : theme.palette.orange.shade40;
  return renderStatusOverlay('snoozeFilled', iconColor, actionName, requester, isHorizontal);
}

function renderTrashedOverlay({requester, isSelected, theme, isHorizontal}: ThemedCellOverlayProps) {
  const actionName = <Trans id="cell-overlay-trashed">Trashed</Trans>;
  const iconColor = isSelected ? theme.invertedText : theme.palette.red.shade40;
  return renderStatusOverlay('trashFilled', iconColor, actionName, requester, isHorizontal);
}

function renderRestoredOverlay({requester, isSelected, theme, isHorizontal}: ThemedCellOverlayProps) {
  const actionName = <Trans id="cell-overlay-restored">Reopened</Trans>;
  const iconColor = isSelected ? theme.invertedText : theme.greys.shade70;
  return renderStatusOverlay('inboxFilled', iconColor, actionName, requester, isHorizontal);
}

function renderStatusOverlay(
  icon: IconName,
  iconColor: string,
  actionName: ReactNode,
  requester?: TeammateModel,
  isHorizontal?: boolean,
) {
  // If there is no requester, render a generic message.
  if (!requester) {
    return (
      <>
        <Icon name={icon} color={iconColor} />
        <StyledDescriptionDiv $isHorizontal={isHorizontal}>
          <strong>{actionName}</strong>
        </StyledDescriptionDiv>
      </>
    );
  }

  // Otherwise, include the requester's name.
  const teammateName = buildTeammateDisplayName(requester);
  return (
    <>
      <Icon name={icon} color={iconColor} />
      <StyledDescriptionDiv $isHorizontal={isHorizontal}>
        <Trans id="cell-overlay-status">
          <strong>{actionName}</strong> by {teammateName}
        </Trans>
      </StyledDescriptionDiv>
    </>
  );
}
